/**
 * PrintA4Page - Druckseite für A4-Aushänge
 * 
 * WICHTIG: Diese Seite ist KOMPLETT isoliert von der Haupt-App.
 * Alle Styles sind direkt eingebettet - keine Tailwind, kein shadcn, keine externen CSS.
 * Screen-Vorschau und Druckausgabe MÜSSEN identisch sein.
 * 
 * Debug-Modus: ?debug=1 zeigt technische Infos an (ohne Druck)
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';

const API = '/api';

// CSS Version für Debug - bei jeder Änderung hochzählen!
const CSS_VERSION = '3.0.0-print-fix';

const PrintA4Page = () => {
  const pathParts = window.location.pathname.split('/');
  const propertyId = pathParts[pathParts.length - 1];
  const urlParams = new URLSearchParams(window.location.search);
  const debugMode = urlParams.get('debug') === '1';
  
  const [property, setProperty] = useState(null);
  const [cleaningCompany, setCleaningCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);
  const [debugInfo, setDebugInfo] = useState({});
  const containerRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [propertyId]);

  // Warten bis alles geladen ist, dann erst drucken (NUR wenn NICHT im Debug-Modus)
  useEffect(() => {
    if (property && !loading && !debugMode) {
      waitForResources().then(() => {
        setReady(true);
        // Kurze Verzögerung für Rendering
        setTimeout(() => {
          window.print();
        }, 500);
      });
    }
  }, [property, loading, debugMode]);

  useEffect(() => {
    const handleAfterPrint = () => {
      if (!debugMode) {
        window.close();
      }
    };
    window.addEventListener('afterprint', handleAfterPrint);
    return () => window.removeEventListener('afterprint', handleAfterPrint);
  }, [debugMode]);

  // Debug-Informationen sammeln - ERWEITERT
  useEffect(() => {
    if (debugMode) {
      // Prüfen ob Print-Media-Query aktiv ist
      const printMediaQuery = window.matchMedia('print');
      
      // Computed Styles für @page auslesen (soweit möglich)
      const computedInfo = {
        documentURL: document.URL,
        devicePixelRatio: window.devicePixelRatio,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
        cssVersion: CSS_VERSION,
        printMediaActive: printMediaQuery.matches,
        userAgent: navigator.userAgent.substring(0, 100),
        pageSize: 'A4 (210mm × 297mm)',
        pageMargins: '12mm top/bottom, 15mm left/right',
        printStylesLoaded: true,
        routePath: window.location.pathname,
      };
      setDebugInfo(computedInfo);
      
      // Listener für Print-Media-Änderungen
      const handlePrintChange = (e) => {
        setDebugInfo(prev => ({ ...prev, printMediaActive: e.matches }));
      };
      printMediaQuery.addEventListener('change', handlePrintChange);
      return () => printMediaQuery.removeEventListener('change', handlePrintChange);
    }
  }, [debugMode]);

  // Warten auf Fonts und Bilder
  const waitForResources = async () => {
    try {
      // Warten auf Fonts
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      
      // Warten auf alle Bilder
      const images = document.querySelectorAll('img');
      const imagePromises = Array.from(images).map(img => {
        if (img.complete) return Promise.resolve();
        return new Promise(resolve => {
          img.onload = resolve;
          img.onerror = resolve;
        });
      });
      await Promise.all(imagePromises);
      
      // Warten auf Canvas-Elemente (QR-Codes)
      await new Promise(resolve => setTimeout(resolve, 100));
      
    } catch (e) {
      console.log('Resource loading:', e);
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Nicht angemeldet');
        setLoading(false);
        return;
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const propRes = await axios.get(`${API}/properties/${propertyId}`);
      setProperty(propRes.data);

      if (propRes.data.cleaning_company_id) {
        try {
          const compRes = await axios.get(`${API}/cleaning-companies/${propRes.data.cleaning_company_id}`);
          setCleaningCompany(compRes.data);
        } catch (e) {
          console.log('Cleaning company not found');
        }
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Fehler beim Laden der Daten');
      setLoading(false);
    }
  };

  // Text verarbeiten - Platzhalter entfernen
  const processWelcomeText = (text) => {
    if (!text) return '';
    return text
      .replace(/\{\{qrCode\}\}/g, '')
      .replace(/\{\{qr_aufgaben\}\}/g, '')
      .replace(/\{\{qr_abreise\}\}/g, '')
      .replace(/\{\{reinigungsfirma\}\}/g, '')
      .replace(/-{10,}/g, '')
      .replace(/Schadensmeldung[\s\S]*$/i, '')
      .trim();
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: 'Arial, sans-serif' }}>
        Lade Druckvorschau...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: 'Arial, sans-serif', color: '#dc2626' }}>
        {error}
      </div>
    );
  }

  if (!property) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: 'Arial, sans-serif' }}>
        Objekt nicht gefunden
      </div>
    );
  }

  const baseUrl = window.location.origin;
  const taskUrl = `${baseUrl}/scan/${propertyId}`;
  const departureUrl = `${baseUrl}/departure/${propertyId}`;
  const welcomeText = processWelcomeText(property.welcome_text || '');

  // ============================================
  // CRITICAL CSS - Version 3.0.0
  // 
  // WICHTIG: Diese Styles sind 1:1 für Screen UND Print!
  // Browser-Header/Footer werden durch @page-Margins kontrolliert.
  // Alle Layout-Maße in mm für physische Konsistenz.
  // ============================================
  const criticalCSS = `
    /* ============================================
       CRITICAL PRINT CSS - Version ${CSS_VERSION}
       Isolierte Styles - KEINE externen Abhängigkeiten!
       ============================================ */
    
    /* RESET - Alles auf Null setzen */
    *, *::before, *::after {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    /* ============================================
       @PAGE SETUP - Kontrolliert Browser-Druck
       - size: exakte A4-Maße
       - margin: definiert den Druckbereich (Browser-Header/Footer liegen AUßERHALB)
       ============================================ */
    @page {
      size: 210mm 297mm; /* A4 exakt */
      margin: 12mm 15mm 12mm 15mm; /* top right bottom left */
    }
    
    /* ============================================
       HTML/BODY SETUP
       - Einheitliche Font-Familie mit Fallbacks
       - print-color-adjust für Hintergrundfarben
       ============================================ */
    html {
      font-size: 11pt;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    
    body {
      /* Font-Stack: System-Fonts die GARANTIERT verfügbar sind */
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif;
      font-size: 11pt;
      line-height: 1.45;
      color: #1e293b;
      background: white;
      margin: 0;
      padding: 0;
      /* Verhindert Text-Rendering-Unterschiede */
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }
    
    /* ============================================
       CONTAINER - A4 Inhaltsbereich
       - Breite: 210mm - 30mm (Ränder) = 180mm
       - Höhe: 297mm - 24mm (Ränder) = 273mm
       ============================================ */
    .print-page {
      width: 180mm;
      min-height: 273mm;
      margin: 0 auto;
      padding: 0;
      background: white;
    }
    
    /* ============================================
       HEADER SECTION - Logo zentriert
       ============================================ */
    .print-header {
      text-align: center;
      margin-bottom: 6mm;
      padding-bottom: 3mm;
      border-bottom: 0.5mm solid #0E4C92;
    }
    
    .print-logo-img {
      max-width: 60mm;
      height: auto;
      margin: 0 auto;
      display: block;
    }
    
    /* ============================================
       TITLE SECTION - Hauptüberschrift
       ============================================ */
    .print-title {
      text-align: center;
      margin-bottom: 4mm;
    }
    
    .print-title-main {
      font-size: 20pt;
      font-weight: bold;
      color: #0E4C92;
      margin-bottom: 2mm;
    }
    
    .print-title-sub {
      font-size: 9pt;
      color: #64748b;
    }
    
    /* ============================================
       CONTENT SECTION - Begrüßungstext
       ============================================ */
    .print-content {
      font-size: 10.5pt;
      line-height: 1.5;
      margin-bottom: 5mm;
      white-space: pre-line;
    }
    
    /* ============================================
       CLEANING BOX - Reinigungsfirma
       ============================================ */
    .print-cleaning-box {
      background-color: #f0f9ff;
      border: 0.4mm solid #0E4C92;
      border-radius: 2mm;
      padding: 4mm 5mm;
      margin: 5mm 0;
    }
    
    .print-cleaning-title {
      font-size: 10pt;
      font-weight: 600;
      color: #0E4C92;
      margin-bottom: 1.5mm;
    }
    
    .print-cleaning-name {
      font-size: 10.5pt;
      font-weight: 600;
      color: #1e293b;
      margin-bottom: 1mm;
    }
    
    .print-cleaning-contact {
      font-size: 9pt;
      color: #64748b;
      line-height: 1.4;
    }
    
    /* ============================================
       DAMAGE SECTION - Schadensmeldung
       ============================================ */
    .print-damage {
      margin: 5mm 0 3mm 0;
    }
    
    .print-damage-title {
      font-size: 10.5pt;
      font-weight: 600;
      color: #0E4C92;
      margin-bottom: 1.5mm;
    }
    
    .print-damage-text {
      font-size: 9.5pt;
      color: #1e293b;
      line-height: 1.4;
    }
    
    /* ============================================
       QR CODE SECTION
       ============================================ */
    .print-qr-box {
      background-color: #f8fafc;
      border: 0.3mm solid #e2e8f0;
      border-radius: 2mm;
      padding: 4mm;
      margin: 4mm 0;
    }
    
    .print-qr-container {
      display: flex;
      justify-content: center;
      gap: 20mm;
    }
    
    .print-qr-item {
      text-align: center;
    }
    
    .print-qr-label {
      font-size: 11pt;
      font-weight: 600;
      color: #0E4C92;
      margin-bottom: 2.5mm;
    }
    
    .print-qr-code {
      width: 30mm;
      height: 30mm;
    }
    
    .print-qr-code canvas {
      width: 30mm !important;
      height: 30mm !important;
    }
    
    .print-qr-instructions {
      text-align: center;
      font-size: 9pt;
      color: #64748b;
      margin-top: 3mm;
    }
    
    /* ============================================
       FOOTER
       ============================================ */
    .print-footer {
      text-align: center;
      font-size: 8pt;
      color: #94a3b8;
      margin-top: 5mm;
      padding-top: 3mm;
      border-top: 0.2mm solid #e2e8f0;
    }
    
    /* ============================================
       BUTTONS - nur auf Screen sichtbar
       ============================================ */
    .print-actions {
      position: fixed;
      top: 10px;
      right: 10px;
      display: flex;
      gap: 8px;
      z-index: 9999;
    }
    
    .print-btn {
      padding: 10px 20px;
      background: #0E4C92;
      color: white;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
    }
    
    .print-btn:hover {
      background: #0A3A70;
    }
    
    .close-btn {
      padding: 10px 20px;
      background: #64748b;
      color: white;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
    }
    
    .close-btn:hover {
      background: #475569;
    }
    
    /* Debug Box */
    .debug-box {
      position: fixed;
      top: 60px;
      right: 10px;
      background: #fef3c7;
      border: 2px solid #f59e0b;
      border-radius: 6px;
      padding: 12px 16px;
      font-size: 11px;
      font-family: 'Courier New', monospace;
      z-index: 9999;
      max-width: 350px;
      line-height: 1.5;
    }
    
    .debug-box strong {
      display: block;
      margin-bottom: 8px;
      font-size: 12px;
      color: #b45309;
    }
    
    .debug-box .debug-row {
      display: flex;
      justify-content: space-between;
      padding: 2px 0;
      border-bottom: 1px dotted #fcd34d;
    }
    
    .debug-box .debug-label {
      color: #92400e;
    }
    
    .debug-box .debug-value {
      color: #1e293b;
      font-weight: 600;
    }
    
    /* ============================================
       @MEDIA PRINT
       
       WICHTIG: Minimale Overrides!
       Die Basis-Styles oben gelten 1:1 auch für Print.
       Hier nur Dinge die SPEZIFISCH für den Druck sind.
       ============================================ */
    @media print {
      /* UI-Elemente komplett entfernen */
      .print-actions,
      .debug-box {
        display: none !important;
        visibility: hidden !important;
      }
      
      /* Body/HTML Margins entfernen - @page kontrolliert die Ränder */
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        background: white !important;
        width: 100% !important;
        height: 100% !important;
      }
      
      /* Container: GLEICHE Breite wie Screen (180mm)
         Keine Skalierung, keine Anpassung! */
      .print-page {
        width: 180mm !important;
        min-height: auto !important;
        max-width: 180mm !important;
        margin: 0 auto !important;
        padding: 0 !important;
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
      
      /* Hintergrundfarben MÜSSEN gedruckt werden */
      .print-cleaning-box,
      .print-qr-box {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
      
      /* QR-Codes exakte Größe beibehalten */
      .print-qr-code,
      .print-qr-code canvas {
        width: 30mm !important;
        height: 30mm !important;
      }
    }
    
    /* ============================================
       @MEDIA SCREEN
       
       Nur für Vorschau-Darstellung im Browser.
       Zeigt das A4-Blatt auf grauem Hintergrund.
       ============================================ */
    @media screen {
      body {
        padding: 20px;
        background: #e5e7eb;
        min-height: 100vh;
      }
      
      .print-page {
        background: white;
        box-shadow: 0 4px 20px rgba(0,0,0,0.15);
        /* Padding simuliert die @page margins für Screen */
        padding: 12mm 15mm;
        border-radius: 2px;
      }
    }
  `;

  return (
    <>
      {/* Critical CSS direkt eingebettet */}
      <style dangerouslySetInnerHTML={{ __html: criticalCSS }} />
      
      {/* Debug Info - ERWEITERT */}
      {debugMode && (
        <div className="debug-box">
          <strong>🔍 Debug Info (CSS v{CSS_VERSION})</strong>
          <div className="debug-row">
            <span className="debug-label">URL:</span>
            <span className="debug-value" style={{fontSize: '9px', wordBreak: 'break-all'}}>{debugInfo.documentURL}</span>
          </div>
          <div className="debug-row">
            <span className="debug-label">Route:</span>
            <span className="debug-value">{debugInfo.routePath}</span>
          </div>
          <div className="debug-row">
            <span className="debug-label">DPR:</span>
            <span className="debug-value">{debugInfo.devicePixelRatio}</span>
          </div>
          <div className="debug-row">
            <span className="debug-label">Page Size:</span>
            <span className="debug-value">{debugInfo.pageSize}</span>
          </div>
          <div className="debug-row">
            <span className="debug-label">Page Margins:</span>
            <span className="debug-value">{debugInfo.pageMargins}</span>
          </div>
          <div className="debug-row">
            <span className="debug-label">Screen:</span>
            <span className="debug-value">{debugInfo.screenWidth}×{debugInfo.screenHeight}</span>
          </div>
          <div className="debug-row">
            <span className="debug-label">Window:</span>
            <span className="debug-value">{debugInfo.innerWidth}×{debugInfo.innerHeight}</span>
          </div>
          <div className="debug-row">
            <span className="debug-label">Print Active:</span>
            <span className="debug-value" style={{color: debugInfo.printMediaActive ? '#16a34a' : '#dc2626'}}>
              {String(debugInfo.printMediaActive)}
            </span>
          </div>
          <div className="debug-row">
            <span className="debug-label">Print CSS:</span>
            <span className="debug-value" style={{color: '#16a34a'}}>✓ Loaded</span>
          </div>
          <div style={{marginTop: '8px', fontSize: '9px', color: '#6b7280'}}>
            UA: {debugInfo.userAgent}
          </div>
        </div>
      )}

      {/* Print Buttons */}
      <div className="print-actions">
        <button className="print-btn" onClick={() => window.print()}>
          Drucken
        </button>
        <button className="close-btn" onClick={() => window.close()}>
          Schließen
        </button>
      </div>

      {/* A4 Seite */}
      <div className="print-page" ref={containerRef}>
        
        {/* Header mit Logo - ZENTRIERT */}
        <div className="print-header">
          <img 
            src="https://customer-assets.emergentagent.com/job_sylt-task-manager/artifacts/kao6q6ly_GBLOGO01.jpg" 
            alt="GB Sylt - Mitza Ferienobjekte Immobilien" 
            className="print-logo-img"
          />
        </div>

        {/* Hauptüberschrift */}
        <div className="print-title">
          <div className="print-title-main">Moin und willkommen!</div>
          <div className="print-title-sub">
            {property.name} • {property.street}, {property.city}
          </div>
        </div>

        {/* Begrüßungstext */}
        {welcomeText && (
          <div className="print-content">{welcomeText}</div>
        )}

        {/* Reinigungsfirma Box */}
        {cleaningCompany && (
          <div className="print-cleaning-box">
            <div className="print-cleaning-title">Ihre Reinigungsfirma:</div>
            <div className="print-cleaning-name">{cleaningCompany.name}</div>
            <div className="print-cleaning-contact">
              {cleaningCompany.contact_person && <div>Ansprechpartner: {cleaningCompany.contact_person}</div>}
              {cleaningCompany.phone && <div>Tel: {cleaningCompany.phone}</div>}
              {cleaningCompany.email && <div>E-Mail: {cleaningCompany.email}</div>}
            </div>
          </div>
        )}

        {/* Schadensmeldung */}
        <div className="print-damage">
          <div className="print-damage-title">Schadensmeldung an das GB Sylt Team</div>
          <div className="print-damage-text">
            Scannen Sie den QR-Code, um Schäden direkt zu melden. Die Information wird automatisch an die Reinigungsfirma gesendet.
          </div>
        </div>

        {/* QR-Codes */}
        <div className="print-qr-box">
          <div className="print-qr-container">
            <div className="print-qr-item">
              <div className="print-qr-label">Problem melden</div>
              <div className="print-qr-code">
                <QRCodeCanvas 
                  value={taskUrl} 
                  size={113} /* 30mm bei 96dpi */
                  level="M"
                  includeMargin={false}
                />
              </div>
            </div>
            <div className="print-qr-item">
              <div className="print-qr-label">Abreise mitteilen</div>
              <div className="print-qr-code">
                <QRCodeCanvas 
                  value={departureUrl} 
                  size={113} /* 30mm bei 96dpi */
                  level="M"
                  includeMargin={false}
                />
              </div>
            </div>
          </div>
          <div className="print-qr-instructions">
            Scannen Sie den QR-Code mit Ihrem Smartphone
          </div>
        </div>

        {/* Footer */}
        <div className="print-footer">
          GB Sylt • Objekt- & Aufgabenmanagement
        </div>
        
      </div>
    </>
  );
};

export default PrintA4Page;
